/**
 * Types for the slide export system.
 *
 * Supports exporting plays, documents, and formations to PPTX and PDF
 * via a content selection → layout configuration → preview → export flow.
 */

/** Number of plays per slide */
export type SlideLayout = 1 | 2 | 4 | 6

/** Supported export file formats */
export type ExportFormat = 'pptx' | 'pdf'

/** Which metadata fields to include on each slide */
export interface MetadataOptions {
	showPlayName: boolean
	showFormation: boolean
	showPersonnel: boolean
	showPlayType: boolean
	showLabels: boolean
	showNotes: boolean
}

/** A content slide containing play diagrams */
export interface ContentSlide {
	type: 'content'
	/** Block IDs to render on this slide (1-6) */
	blockIds: string[]
}

/** A section divider slide */
export interface DividerSlide {
	type: 'divider'
	/** Divider title text */
	title: string
}

/** A single slide in the export configuration */
export type ExportSlide = ContentSlide | DividerSlide

/** Full configuration for a slide export request */
export interface SlideExportConfig {
	/** Ordered list of slides to generate */
	slides: ExportSlide[]
	/** Output format */
	format: ExportFormat
	/** Which metadata fields to include */
	metadata: MetadataOptions
	/** Whether to include section divider slides */
	includeSectionDividers: boolean
	/** Deck title (used on title slide if present) */
	title: string
}

/** Request payload sent to the export API */
export interface SlideExportRequest {
	playbookId: string
	config: SlideExportConfig
}

/** Metadata resolved for a block during export */
export interface ExportBlockMetadata {
	name: string
	formation?: string
	personnel?: string
	playType?: string
	labels?: string[]
	notes?: string
}

/** Default metadata options — play name and formation shown */
export const DEFAULT_METADATA_OPTIONS: MetadataOptions = {
	showPlayName: true,
	showFormation: true,
	showPersonnel: false,
	showPlayType: false,
	showLabels: false,
	showNotes: false,
}
